import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(CategoryScale, ArcElement, Tooltip, Legend);

const SaltDoughCalculator = () => {
  const [doughData, setDoughData] = useState({
    flour: '',
    salt: '',
    water: '',
    unit: 'grams',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDoughData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleUnitChange = (e) => {
    setDoughData(prev => ({
      ...prev,
      unit: e.target.value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setDoughData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateDoughProperties = () => {
    const { flour, salt, water, unit } = doughData;

    if (!flour || !salt || !water) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const f = parseFloat(flour);
      const s = parseFloat(salt);
      const w = parseFloat(water);

      if (isNaN(f) || isNaN(s) || isNaN(w)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      const totalWeight = f + s + w;
      const flourPercent = (f / totalWeight * 100).toFixed(1);
      const saltPercent = (s / totalWeight * 100).toFixed(1);
      const waterPercent = (w / totalWeight * 100).toFixed(1);

      const hydrationLevel = (w / f * 100).toFixed(1);
      const saltToFlourRatio = (s / f * 100).toFixed(1);

      const generateChartData = () => {
        return {
          labels: ['Flour', 'Salt', 'Water'],
          datasets: [{
            data: [f, s, w],
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
            borderWidth: 1
          }]
        };
      };

      setResults({
        totalWeight: totalWeight.toFixed(1),
        flourPercent,
        saltPercent,
        waterPercent,
        hydrationLevel,
        saltToFlourRatio
      });
      setChartData(generateChartData());

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Salt Dough Calculator | Dough Proportions Tool</title>
        <meta name="description" content="Calculate salt dough proportions with our precision calculator. Analyze flour, salt, and water ratios for perfect dough consistency." />
        <meta name="keywords" content="salt dough calculator, dough proportions, baking calculator, flour salt water ratio" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-yellow-500 mr-4">🍞</div>
            <h1 className="text-3xl font-bold text-gray-800">Salt Dough Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>This calculator helps you analyze salt dough proportions and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Total dough weight calculation</li>
              <li>✓ Ingredient percentage breakdown</li>
              <li>✓ Hydration level assessment</li>
              <li>✓ Salt to flour ratio analysis</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flour Amount:</label>
                <input
                  type="number"
                  id="flour"
                  name="flour"
                  value={doughData.flour}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter flour amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salt Amount:</label>
                <input
                  type="number"
                  id="salt"
                  name="salt"
                  value={doughData.salt}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter salt amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Water Amount:</label>
                <input
                  type="number"
                  id="water"
                  name="water"
                  value={doughData.water}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter water amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit:</label>
                <select
                  id="unit"
                  name="unit"
                  value={doughData.unit}
                  onChange={handleUnitChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="grams">Grams</option>
                  <option value="cups">Cups</option>
                  <option value="ounces">Ounces</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={doughData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and statistical breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculateDoughProperties}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Dough Properties
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Dough Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Weight</h3>
                    <p className="text-gray-600">{results.totalWeight} {doughData.unit}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Hydration Level</h3>
                    <p className="text-gray-600">{results.hydrationLevel}%</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Pie
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Ingredient Proportions',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Detailed Analysis</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Flour %:</span>
                      {results.flourPercent}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Salt %:</span>
                      {results.saltPercent}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Water %:</span>
                      {results.waterPercent}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Salt to Flour Ratio:</span>
                      {results.saltToFlourRatio}%
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Salt Dough Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600 mb-8">
              <p>Follow these steps to use the calculator:</p>
              <ol className="list-decimal pl-6 mt-2">
                <li>Enter the amount of flour, salt, and water you're using</li>
                <li>Select your preferred unit of measurement</li>
                <li>Click "Calculate Dough Properties"</li>
                <li>Review the analysis results and proportions</li>
              </ol>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">What is hydration level?</h3>
                <p>The percentage of water relative to flour weight in your dough</p>
              </div>
              <div>
                <h3 className="font-semibold">What's a good salt to flour ratio?</h3>
                <p>Typically 1.8-2.2% salt by weight relative to flour</p>
              </div>
              <div>
                <h3 className="font-semibold">Can I use volume measurements?</h3>
                <p>Yes, but weight measurements are more accurate for baking</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc pl-6">
                <li>Flour: 500g</li>
                <li>Salt: 10g</li>
                <li>Water: 300g</li>
              </ul>
              <p className="mt-4"><strong>Results:</strong></p>
              <ul className="list-disc pl-6">
                <li>Total weight: 810g</li>
                <li>Hydration level: 60%</li>
                <li>Salt to flour ratio: 2%</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaltDoughCalculator;